import { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Loader2, Ticket } from 'lucide-react';
import {
  useCreateIncidentTicket,
  useIncidentTickets,
  useLinkIncidentTicket,
  type AvailableConnection,
  type IncidentTicket,
} from '@/hooks/usePsa.ts';
import { usePermission } from '@/hooks/usePermission.ts';
import { apiErrorCode, apiErrorMessage } from '@/utils/apiError.ts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import axios from 'axios';
import { format } from 'date-fns';

/**
 * The incident's tickets in the MSP's PSA, and Create ticket / Link ticket
 * (docs/PsaIntegration.md, "Creating tickets").
 *
 * Shown only when the organization has a connection that serves this incident;
 * otherwise it renders nothing, since there is nowhere for a ticket to go. Creating
 * or linking takes `incident:update`. The caller gates on the `psa_ticketing` rollout.
 */
export function IncidentTickets({ incidentId }: { incidentId: string }) {
  const tickets = useIncidentTickets(incidentId, true);
  const canUpdate = usePermission('incident:update');
  const data = tickets.data;
  if (!data || (data.tickets.length === 0 && data.connections.length === 0)) return null;

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-3">
      <h2 className="text-sm font-semibold">Ticket</h2>
      {data.tickets.map((t) =>
        t.ticketRef === null && t.createAfter ? (
          <MakeTicket
            key={t.id}
            incidentId={incidentId}
            connection={{ id: t.connectionId, name: t.connectionName, kind: t.kind, status: 'active' }}
            canUpdate={canUpdate}
            pending={t}
          />
        ) : (
          <BoundTicket key={t.id} ticket={t} />
        ),
      )}
      {data.connections.map((c) => (
        <MakeTicket key={c.id} incidentId={incidentId} connection={c} canUpdate={canUpdate} />
      ))}
    </section>
  );
}

function BoundTicket({ ticket }: { ticket: IncidentTicket }) {
  const how = ticket.createdVia === 'linked' ? 'linked' : 'created from Witina';
  return (
    <div className="text-sm space-y-1">
      <p className="flex items-center gap-2">
        <Ticket className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium">{ticket.ticketNumber ?? 'Being created…'}</span>
        <span className="text-muted-foreground">in {ticket.connectionName}</span>
      </p>
      <p className="text-xs text-muted-foreground">
        {ticket.closedInPsaAt ? 'Closed in the PSA. ' : ''}
        {how[0].toUpperCase() + how.slice(1)}
        {ticket.incidentCount > 1 ? `; carries ${String(ticket.incidentCount)} incidents` : ''}. Comments, acknowledgement,
        severity and recovery are added to it as notes.
      </p>
      {ticket.lastError ? (
        <p className="text-xs text-destructive flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Last refusal: {ticket.lastError}</span>
        </p>
      ) : null}
    </div>
  );
}

interface CompanyMismatch {
  ticketCompany: string;
  mappedCompany: string;
}

function mismatchOf(err: unknown): CompanyMismatch | null {
  if (apiErrorCode(err) !== 'company_mismatch' || !axios.isAxiosError(err)) return null;
  return err.response?.data as CompanyMismatch;
}

/**
 * Create ticket and Link ticket for one connection. With `pending`, a route's ticket
 * is waiting out the settle delay: Create now makes it at once, and linking an
 * existing ticket replaces it. Either takes the pending ticket, never a second one.
 */
function MakeTicket({
  incidentId,
  connection,
  canUpdate,
  pending,
}: {
  incidentId: string;
  connection: AvailableConnection;
  canUpdate: boolean;
  pending?: IncidentTicket;
}) {
  const create = useCreateIncidentTicket(incidentId);
  const link = useLinkIncidentTicket(incidentId);
  const [linking, setLinking] = useState(false);
  const [number, setNumber] = useState('');

  if (connection.status !== 'active') {
    return (
      <p className="text-xs text-muted-foreground">
        {connection.name} needs an admin’s attention before tickets can be made there.
      </p>
    );
  }
  const waiting = pending?.createAfter ? (
    <p className="text-xs text-muted-foreground">
      A ticket will be made in {connection.name} at {format(new Date(pending.createAfter), 'p')} if this is still
      open then.
      {pending.lastError ? ` Last refusal: ${pending.lastError}` : ''}
    </p>
  ) : null;
  if (!canUpdate) {
    return waiting ?? <p className="text-xs text-muted-foreground">No ticket in {connection.name} yet.</p>;
  }
  const error = create.error ?? link.error;
  const mismatch = mismatchOf(link.error);
  const busy = create.isPending || link.isPending;

  return (
    <div className="space-y-2">
      {waiting}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy}
          onClick={() => { link.reset(); create.mutate(connection.id); }}>
          {create.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ticket className="w-3.5 h-3.5" />}
          {pending ? 'Create now' : `Create ticket in ${connection.name}`}
        </Button>
        {!linking ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setLinking(true); }}>
            Link existing ticket
          </Button>
        ) : null}
      </div>
      {linking ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            create.reset();
            link.mutate({ connectionId: connection.id, ticketNumber: number.trim() });
          }}
        >
          <Input aria-label="Ticket number" placeholder="T20261019.0042" className="h-8" value={number}
            onChange={(e) => { setNumber(e.target.value); link.reset(); }} />
          <Button type="submit" size="sm" disabled={!number.trim() || busy}>
            {link.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Link
          </Button>
        </form>
      ) : null}
      {mismatch ? (
        <div className="text-xs space-y-2">
          <p>
            That ticket belongs to another company in {connection.name} than this customer’s
            ({mismatch.ticketCompany}, not {mismatch.mappedCompany}).
          </p>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={() => { link.mutate({ connectionId: connection.id, ticketNumber: number.trim(), confirmCompany: true }); }}>
            Link anyway
          </Button>
        </div>
      ) : error ? (
        <p className="text-xs text-destructive">
          {apiErrorMessage(error, 'That didn’t work.')}
          {apiErrorCode(error) === 'not_mapped' ? (
            <>
              {' '}
              <Link to="/admin/notifications/psa" className="underline">Map it</Link>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
